import { SMS_LENGTH_WARNING_THRESHOLD, SMS_MAX_LENGTH } from "@sp-crm/core";
import {
    PlaceholderInsertTarget,
    PlaceholderPopover,
} from "components/editor/placeholder-popover";
import { Ribbon } from "components/editor/ribbon";
import { TextArea } from "components/ui/textarea";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";

interface SmsBodySettingProps {
    label: string;
    value: string;
    onCommit: (newValue: string) => void;
    disabled: boolean;
    placeholderTypes: AdvancedSearchEntityType[];
}

export const SmsBodySetting: React.FC<SmsBodySettingProps> = props => {
    const { label, value, onCommit, disabled, placeholderTypes } = props;
    const [draft, setDraft] = React.useState(value);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const selectionRef = React.useRef<{ start: number; end: number } | null>(null);

    React.useEffect(() => {
        setDraft(value);
    }, [value]);

    const insertAtCursor = (token: string) => {
        const selection = selectionRef.current ?? {
            start: draft.length,
            end: draft.length,
        };
        const next = draft.slice(0, selection.start) + token + draft.slice(selection.end);
        selectionRef.current = {
            start: selection.start + token.length,
            end: selection.start + token.length,
        };
        setDraft(next);
        onCommit(next);
    };

    const focusBody = () => {
        const textarea = containerRef.current?.querySelector("textarea");
        if (!textarea) {
            return;
        }
        textarea.focus();
        requestAnimationFrame(() => {
            const selection = selectionRef.current;
            if (selection) {
                textarea.setSelectionRange(selection.start, selection.end);
            }
        });
    };

    const insertTarget: PlaceholderInsertTarget = {
        insertContent: insertAtCursor,
        focus: focusBody,
    };

    return (
        <div className="space-y-2" ref={containerRef}>
            <div>
                {label ? <div className="mb-1">{label}</div> : null}
                <Ribbon position="top">
                    <PlaceholderPopover
                        editor={insertTarget}
                        placeholderTypes={placeholderTypes}
                        disabled={disabled}
                    />
                </Ribbon>
                <TextArea
                    autoGrow={true}
                    disabled={disabled}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={() => {
                        if (draft !== value) {
                            onCommit(draft);
                        }
                    }}
                    onSelect={e => {
                        selectionRef.current = {
                            start: e.currentTarget.selectionStart,
                            end: e.currentTarget.selectionEnd,
                        };
                    }}
                />
            </div>
            <div
                className={
                    draft.length > SMS_MAX_LENGTH
                        ? "text-xs text-red-500 text-right"
                        : draft.length >= SMS_LENGTH_WARNING_THRESHOLD
                          ? "text-xs text-amber-600 text-right"
                          : "text-xs text-gray-500 text-right"
                }>
                {draft.length}/{SMS_MAX_LENGTH}
                {draft.length >= SMS_LENGTH_WARNING_THRESHOLD
                    ? ` — texts are limited to ${SMS_MAX_LENGTH} characters, and an opt-out notice may be appended for new recipients`
                    : ""}
            </div>
        </div>
    );
};
